import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import FilmCard from '@/components/base/FilmCard';
import ImageLightbox from '@/components/base/ImageLightbox';
import DetailHero from '@/pages/detail/components/DetailHero';
import CastSection from '@/pages/detail/components/CastSection';
import { getFilmById, getRelatedFilms, typeLabel } from '@/mocks/films';
import { isFavorite, toggleFavorite } from '@/utils/favorites';
import { pushHistory } from '@/utils/storage';
import { applySeo } from '@/utils/seo';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const film = getFilmById(id);
  const [favorite, setFavorite] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [shareTip, setShareTip] = useState('');

  useEffect(() => {
    if (!film) {
      applySeo({
        title: '影视详情暂无数据 - 同学的妈妈',
        description: '该影视作品暂未收录，返回首页浏览更多电影与电视剧剧情介绍。',
        keywords: '同学的妈妈,影视大全,剧情介绍',
        path: `/detail/${id ?? ''}`,
      });
      return;
    }
    applySeo({
      title: `${film.title}剧情介绍 - 同学的妈妈`,
      description: `${film.summary}${film.title}由${film.director}执导，${film.cast.map((c) => c.name).join('、')}主演。`,
      keywords: `${film.title},${film.title}剧情介绍,${film.genres.join(',')},同学的妈妈`,
      path: `/detail/${film.id}`,
    });
    setFavorite(isFavorite(film.id));
    pushHistory(film.id);
    setLightbox(false);
  }, [film, id]);

  useEffect(() => {
    if (!shareTip) return;
    const timer = window.setTimeout(() => setShareTip(''), 2400);
    return () => window.clearTimeout(timer);
  }, [shareTip]);

  if (!film) {
    return (
      <div className="px-4 py-10 md:px-8 lg:px-12">
        <Breadcrumb items={[{ label: '影视详情' }]} />
        <div className="mt-6">
          <EmptyState
            title="影视详情暂无数据"
            description="这部作品可能已经下架或链接有误，去首页看看其他影视内容吧。"
            resetLabel="返回首页"
            resetTo="/"
          />
        </div>
      </div>
    );
  }

  const related = getRelatedFilms(film, 6);

  const handleFavorite = () => {
    setFavorite(toggleFavorite(film.id));
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, '')}/detail/${film.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareTip('链接已复制到剪贴板');
    } catch {
      setShareTip(`分享链接：${url}`);
    }
  };

  return (
    <div>
      <DetailHero
        film={film}
        onPreview={() => setLightbox(true)}
        favorite={favorite}
        onToggleFavorite={handleFavorite}
        onShare={handleShare}
        shareTip={shareTip}
      />

      <div className="px-4 pt-5 md:px-8 lg:px-12">
        <Breadcrumb
          items={[
            { label: typeLabel[film.type], to: `/category/${film.type}` },
            { label: film.title },
          ]}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-6 px-4 md:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <div className="lg:col-span-8">
          <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <div className="flex items-center justify-between border-b border-background-200 pb-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                {film.title} · 剧情介绍
              </h2>
              <span className="font-label text-[11px] text-foreground-400">共 {film.synopsis.length} 段</span>
            </div>
            <div className="mt-4 space-y-4">
              {film.synopsis.map((paragraph, index) => (
                <p key={index} className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-6 rounded-md bg-background-100 px-3.5 py-3">
              <p className="text-[11px] leading-relaxed text-foreground-500">
                以上剧情介绍由编辑整理撰写，可能包含部分剧透。本站不提供任何影音文件与播放服务。
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {film.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-accent-50 px-2 py-1 text-[11px] text-accent-800"
                >
                  # {tag}
                </span>
              ))}
            </div>
          </article>
        </div>

        <div className="flex flex-col gap-4 lg:col-span-4">
          <CastSection cast={film.cast} director={film.director} writers={film.writers} />

          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">作品信息</h2>
            <dl className="mt-3 space-y-2.5 text-xs">
              {[
                { label: '作品类型', value: typeLabel[film.type] },
                { label: '上映/首播', value: film.releaseDate },
                { label: '地区/语言', value: `${film.region} · ${film.language}` },
                { label: '片长/集数', value: film.duration },
                { label: '热度指数', value: String(film.popularity) },
                { label: '更新日期', value: film.updatedAt },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-3">
                  <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-right font-label font-medium text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-lg bg-secondary-100 p-4 md:p-5">
            <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-secondary-900">
              <i className="ri-lightbulb-line w-4 h-4 flex items-center justify-center" />
              观影提示
            </h2>
            <p className="mt-2 text-[11px] leading-relaxed text-secondary-900/80">
              本片适合在安静的夜晚观看。建议提前关闭弹窗通知，剧情前四十分钟节奏较慢，请耐心进入。
            </p>
            <Link
              to={`/category/${film.type}`}
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-secondary-900 hover:underline cursor-pointer whitespace-nowrap"
            >
              浏览更多{typeLabel[film.type]}
              <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      </div>

      <section className="mt-10 px-4 md:px-8 lg:px-12">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.2em] text-accent-600">You may also like</p>
            <h2 className="mt-1 font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
          </div>
          <p className="hidden text-xs text-foreground-500 sm:block">基于类型与标签匹配</p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
          {related.map((item) => (
            <FilmCard key={item.id} film={item} />
          ))}
        </div>
      </section>

      {lightbox && (
        <ImageLightbox src={film.poster} alt={`${film.title} 高清海报`} onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}